import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import SectionHeader from '@/components/SectionHeader';

interface FAQEntry {
  id: string;
  number: string;
  question: string;
  answer: string;
  bullets: string[];
}

const FAQS: FAQEntry[] = [
  {
    id: 'order',
    number: '01',
    question: 'How do I place an order?',
    answer:
      'Four steps. Researcher account, cart, WhatsApp confirmation, dispatch. The site lists every compound with its current third-party certificate. Payment finalises on WhatsApp because card processors block research-chemical merchants in most countries.',
    bullets: [
      'Account creation requires age and research-use affirmation. Recorded with timestamp and IP.',
      'Payment is finalised in chat: bank transfer, card link, or crypto, depending on destination country.',
      'First WhatsApp reply typically lands within 30 minutes during UAE office hours.',
    ],
  },
  {
    id: 'shipping-time',
    number: '02',
    question: 'How long does shipping take?',
    answer:
      'Dispatched the next UAE business day. The table below covers prep, transit on DHL Express or FedEx International Priority, and a customs buffer.',
    bullets: [
      'Tracking link is emailed within 24 hours of dispatch.',
      'Cold chain held at 2–8 °C door to door: insulated box, gel pack and indicator card.',
    ],
  },
  {
    id: 'shipping-destinations',
    number: '03',
    question: 'Do you ship to my country?',
    answer:
      'Yes, worldwide. The table below lists verified destinations and the courier on each lane. If your country is not listed, message us on WhatsApp and we will quote a route.',
    bullets: [
      'All shipments use the international harmonised code for research chemicals (UN 3316). Customs paperwork is accurate, never falsified.',
      'Import duties and local taxes are the importer\'s responsibility. We do not collect them at checkout.',
    ],
  },
  {
    id: 'shipping-cost',
    number: '04',
    question: 'How much is shipping?',
    answer:
      'Free. Every order. No minimum. The card below details what travels in the box on every shipment.',
    bullets: [
      'Insulated box, gel pack and tamper-evident seal: included.',
      'Carrier is DHL Express or FedEx International Priority depending on the lane.',
      'Local import duties or taxes, where applicable, are charged by the carrier on delivery.',
    ],
  },
  {
    id: 'tracking',
    number: '05',
    question: 'Can I track my order?',
    answer:
      'Yes. The DHL or FedEx tracking link is emailed within 24 hours of dispatch. The same link is mirrored inside your AURA account.',
    bullets: [
      'Visible states: collected, in transit, customs, out for delivery, delivered.',
      'If a status freezes for more than 24 hours we open a carrier trace on your behalf.',
    ],
  },
  {
    id: 'damaged',
    number: '06',
    question: 'What if my vial arrives damaged or warm?',
    answer:
      'Same-day replacement. Send a photo on WhatsApp showing the box and the cold-chain indicator card. We confirm within an hour and dispatch the replacement the same business day.',
    bullets: [
      'Counts as damaged: broken seal, visible breakage or red cold-chain indicator card.',
      'Counts as warm: gel pack fully thawed on arrival.',
      'No forms. No physical return of the original required.',
    ],
  },
  {
    id: 'refund',
    number: '07',
    question: 'What is your refund policy?',
    answer:
      'Three scenarios, three resolutions. All are settled on WhatsApp within one business day. When both options apply, you choose between same-day reship and full refund.',
    bullets: [
      'Refunds return to the original payment method within 5 to 7 business days, depending on the rail.',
      'Chargebacks are slow for both sides. Always open a WhatsApp ticket first.',
    ],
  },
  {
    id: 'support',
    number: '08',
    question: 'How do I contact customer support?',
    answer:
      'WhatsApp is fastest. Email is the second channel. Both are staffed by the AURA team in Dubai. No bots, no outsourced ticket queue.',
    bullets: [
      'We reply in EN, ES, FR, DE and NL.',
      'Lab and chemistry questions are looped to our AURA lab team directly when needed.',
    ],
  },
];

export default function FAQSection() {
  const [openId, setOpenId] = useState<string | null>(null);

  const toggle = (id: string) => {
    setOpenId((current) => (current === id ? null : id));
  };

  return (
    <section
      id="faq"
      className="border-y border-rp-line bg-rp-bone py-10 md:py-20"
    >
      <div className="rp-container">
        <div className="mx-auto max-w-[760px]">
          <SectionHeader
            eyebrow="FAQ"
            title={<span className="font-semibold">Common questions</span>}
            description="The questions the AURA team gets most from procurement teams and principal investigators. Lab-side questions get looped to our AURA lab team directly. If yours isn't here, replies land within 6 business hours."
            align="center"
          />
        </div>

        <div className="mx-auto max-w-[760px] border-t border-rp-line bg-rp-paper">
          {FAQS.map((faq) => {
            const isOpen = openId === faq.id;

            return (
              <div
                key={faq.id}
                className="border-b border-rp-line"
              >
                <button
                  type="button"
                  onClick={() => toggle(faq.id)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-panel-${faq.id}`}
                  className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left md:gap-4 md:px-5 md:py-3.5"
                >
                  <span className="w-9 flex-shrink-0 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-rp-forest">
                    {faq.number}
                  </span>
                  <span className="flex-1 text-sm font-semibold leading-snug text-rp-ink">
                    {faq.question}
                  </span>
                  <span
                    className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-rp-line text-rp-forest transition-colors duration-200"
                    aria-hidden="true"
                  >
                    <AnimatePresence mode="wait" initial={false}>
                      {isOpen ? (
                        <motion.span
                          key="minus"
                          initial={{ opacity: 0, rotate: -90 }}
                          animate={{ opacity: 1, rotate: 0 }}
                          exit={{ opacity: 0, rotate: 90 }}
                          transition={{ duration: 0.15 }}
                        >
                          <Minus size={14} strokeWidth={2} />
                        </motion.span>
                      ) : (
                        <motion.span
                          key="plus"
                          initial={{ opacity: 0, rotate: 90 }}
                          animate={{ opacity: 1, rotate: 0 }}
                          exit={{ opacity: 0, rotate: -90 }}
                          transition={{ duration: 0.15 }}
                        >
                          <Plus size={14} strokeWidth={2} />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      id={`faq-panel-${faq.id}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-0 md:px-5 md:pb-5">
                        <p className="pl-9 text-sm leading-relaxed text-rp-moss md:pl-10">
                          {faq.answer}
                        </p>
                        <ul className="mt-3 flex list-none flex-col gap-2 pl-9 md:pl-10">
                          {faq.bullets.map((bullet, bulletIndex) => (
                            <li
                              key={`${faq.id}-bullet-${bulletIndex}`}
                              className="relative pl-4 text-sm leading-relaxed text-rp-moss"
                            >
                              <span
                                className="absolute left-0 top-[0.55em] h-1 w-1 rounded-full bg-rp-leaf-2"
                                aria-hidden="true"
                              />
                              {bullet}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
